import { useEffect, useMemo, useState } from 'react';
import { useUserProjectsContext } from '../contexts/UserProjectsContext';
import { projectService } from '../services/projectService';
import { demoProjectsService } from '../services/demoProjectsService';
import { cleanBreakdown, deriveElevation, filterBreakdownToVisible, getVisibleArtifactNames, getContourElevationInfo, hasVisibleCombined, isCombinedLayerName, hasVisibleTwi, hasVisibleFlow, getVisibleTwiPercentiles, } from '../utils/legendData';
// Fetches project statistics (when print is open) and derives the print legend
// for the layers/classes that are CURRENTLY VISIBLE. Aspect/slope/geomorphons
// are per-class artifacts, so the breakdown is filtered to the visible classes
// (e.g. only East-facing). Elevation is continuous and shown as a gradient.
export function usePrintLegend(active, loadedArtifacts, isArtifactVisible, selectedProject, artifactControls, visibleFeatures = []) {
    const { projects } = useUserProjectsContext();
    const [stats, setStats] = useState(null);
    const projectId = useMemo(() => {
        var _a;
        try {
            return ((_a = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getId) === null || _a === void 0 ? void 0 : _a.call(selectedProject)) || '';
        }
        catch (_b) {
            return '';
        }
    }, [selectedProject]);
    const isDemo = useMemo(() => (projectId ? !projects.some((p) => p.id === projectId) : false), [projectId, projects]);
    const visibleNames = useMemo(() => getVisibleArtifactNames(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    // Per-class breakdown matching must ignore combined layers, so a combined
    // "Slope"/"Aspect"/"Geomorphons" raster can't false-match a like-named class
    // in another category's breakdown.
    const perClassNames = useMemo(() => {
        const s = new Set();
        visibleNames.forEach((n) => {
            if (!isCombinedLayerName(n))
                s.add(n);
        });
        return s;
    }, [visibleNames]);
    const contourInfo = useMemo(() => getContourElevationInfo(loadedArtifacts, isArtifactVisible, artifactControls), [loadedArtifacts, isArtifactVisible, artifactControls]);
    const showTwi = useMemo(() => hasVisibleTwi(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    const twiPercentiles = useMemo(() => getVisibleTwiPercentiles(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    const showFlow = useMemo(() => hasVisibleFlow(loadedArtifacts, isArtifactVisible), [loadedArtifacts, isArtifactVisible]);
    useEffect(() => {
        if (!active || !projectId || loadedArtifacts.size === 0) {
            setStats(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const fetched = isDemo
                    ? await demoProjectsService.getProjectStatistics(projectId)
                    : await projectService.getProjectStatistics(projectId);
                if (cancelled)
                    return;
                setStats(fetched
                    ? {
                        elevationBreakdown: fetched.elevationBreakdown,
                        slopeBreakdown: fetched.slopeBreakdown,
                        aspectBreakdown: fetched.aspectBreakdown,
                        geomorphonsBreakdown: fetched.geomorphonsBreakdown,
                    }
                    : null);
            }
            catch (e) {
                if (!cancelled) {
                    console.error('Print legend: statistics fetch failed', e);
                    setStats(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // loadedArtifacts.size is the cheap signal that layers changed.
    }, [active, projectId, isDemo, loadedArtifacts.size]);
    // A combined (all-classes) raster shows the full breakdown; otherwise show
    // only the per-class layers that are visible.
    const combinedAspect = useMemo(() => hasVisibleCombined(loadedArtifacts, isArtifactVisible, 'aspect'), [loadedArtifacts, isArtifactVisible]);
    const combinedSlope = useMemo(() => hasVisibleCombined(loadedArtifacts, isArtifactVisible, 'slope'), [loadedArtifacts, isArtifactVisible]);
    const combinedGeomorphons = useMemo(() => hasVisibleCombined(loadedArtifacts, isArtifactVisible, 'geomorphons'), [loadedArtifacts, isArtifactVisible]);
    return useMemo(() => {
        const elevation = contourInfo.showGradient ? deriveElevation(stats === null || stats === void 0 ? void 0 : stats.elevationBreakdown) : null;
        const aspect = combinedAspect
            ? cleanBreakdown(stats === null || stats === void 0 ? void 0 : stats.aspectBreakdown)
            : filterBreakdownToVisible(stats === null || stats === void 0 ? void 0 : stats.aspectBreakdown, perClassNames);
        const slope = combinedSlope
            ? cleanBreakdown(stats === null || stats === void 0 ? void 0 : stats.slopeBreakdown)
            : filterBreakdownToVisible(stats === null || stats === void 0 ? void 0 : stats.slopeBreakdown, perClassNames);
        // A visible combined Geomorphons raster shows the full breakdown; otherwise
        // filter to visible per-class layers. perClassNames excludes combined layers
        // so a combined "Slope" can't surface as a geomorphon "Slope" class.
        const geomorphons = combinedGeomorphons
            ? cleanBreakdown(stats === null || stats === void 0 ? void 0 : stats.geomorphonsBreakdown)
            : filterBreakdownToVisible(stats === null || stats === void 0 ? void 0 : stats.geomorphonsBreakdown, perClassNames);
        const hasAny = !!elevation ||
            contourInfo.hasAny ||
            aspect.length > 0 ||
            slope.length > 0 ||
            geomorphons.length > 0 ||
            showTwi ||
            twiPercentiles.length > 0 ||
            showFlow ||
            visibleFeatures.length > 0;
        return {
            hasAny,
            elevation,
            contourIntervals: contourInfo.contourIntervals,
            monochromeColors: contourInfo.monochromeColors,
            aspect,
            slope,
            geomorphons,
            twi: showTwi,
            twiPercentiles,
            flow: showFlow,
            features: visibleFeatures,
        };
    }, [stats, perClassNames, contourInfo, combinedAspect, combinedSlope, combinedGeomorphons, showTwi, twiPercentiles, showFlow, visibleFeatures]);
}
